// report.ts - the end-of-hand report MODEL, built from REAL accumulated per-street grades (S23).
//
// The DX6 refinement: the mockup's report used a static illustrative fixture (the `REPORT` object in
// local-mode-read-first-v2.html). Here it is computed from the four `ResolvedGrade`s the user actually
// earned by playing through the Diagnose stepper - the same shape the mockup's Solo report already
// computed from real answers (renderSReport, lines ~1399-1417).
//
// Per street the report grades the DECISION as a whole (read + play) - exactly what the card copy claims
// ("Graded on the read and the play, not on whether you won"). That combined grade is the reveal's
// `verdict` move class, so a street where you read him right AND took the best line reads Best -> 100
// (DX8: "Best reads 100 everywhere"), a right play with a slipped read dips to Inaccuracy, and a wrong
// play drops to Blunder.
//
// <EndOfHandReport> renders this model and is REUSED by Solo (S24): Solo builds its own ResolvedGrade[]
// (from a generated hand's engine GradeBand via MOVE_CLASS_FROM_BAND) and passes its own header + verdict
// copy into `buildReport`.
import { MOVE_CLASS_LABELS, type MoveClass, type ResolvedGrade } from '../grade/types';

/** Move class -> accuracy %, transcribed from the mockup ACC map (line 1257). `best` is 100 (DX8). The
 *  Diagnose verdict only ever lands best / inacc / blun, but the full map keeps the report reusable for
 *  Solo's per-street grades (which can be exc/mist/great). */
export const ACC: Record<MoveClass, number> = {
  best: 100,
  great: 82,
  exc: 87,
  inacc: 70,
  mist: 52,
  blun: 34,
  book: 100,
};

/** The four streets, in order. Index matches the Diagnose stepper. */
export const STREET_NAMES = ['Preflop', 'Flop', 'Turn', 'River'] as const;

/** Best -> worst display order for the grade-band chips. */
const BAND_ORDER: MoveClass[] = ['best', 'great', 'exc', 'inacc', 'mist', 'blun', 'book'];

/** One row of the report: the street, its combined move class + accuracy, and the action taken. */
export interface ReportStreet {
  name: string;
  acc: number;
  cls: MoveClass;
  /** the action label shown in the street-by-street row ("Raise to isolate", "Call", "Fold to the jam"). */
  action: string;
}

/** A grade-band count chip (non-zero bands only). */
export type ReportBand = [cls: MoveClass, label: string, count: number];

/** Everything <EndOfHandReport> renders. Header + verdict card copy come from the caller so Solo can
 *  reuse the component with its own voice. */
export interface ReportModel {
  kicker: string;
  h2: string;
  lede: string;
  /** overall hand accuracy % (mean of the per-street accuracies). */
  accuracy: number;
  /** move class driving the big accuracy number's color. */
  badgeCls: MoveClass;
  /** the verdict headline ("Textbook, start to finish"). */
  verdict: string;
  /** the sub line under the verdict. */
  sub: string;
  streets: ReportStreet[];
  bands: ReportBand[];
}

/** Was the play (Axis A) a best or excellent line? (exc = a correct-but-slightly-off-size raise.) */
function playRight(g: ResolvedGrade): boolean {
  return g.axisA.cls === 'best' || g.axisA.cls === 'exc';
}

/** Was the read (Axis B) right? READ_AXIS.right.cls is 'exc'; a missed read grades 'inacc'. */
function readRight(g: ResolvedGrade): boolean {
  return g.axisB.cls === 'exc';
}

/** Project the accumulated grades into the per-street report rows. The combined grade is the verdict
 *  move class; the row action is the play label, annotated when the play was right but the read slipped
 *  (so the dip in the accuracy line is explained). */
export function reportStreets(grades: ResolvedGrade[]): ReportStreet[] {
  return grades.map((g, i) => {
    const cls = g.verdict.cls;
    let action = g.axisA.label;
    if (playRight(g) && !readRight(g)) action += ' . read off';
    return { name: STREET_NAMES[i] ?? `Street ${i + 1}`, acc: ACC[cls], cls, action };
  });
}

/** Non-zero grade-band counts, best -> worst. */
export function bandCounts(streets: ReportStreet[]): ReportBand[] {
  return BAND_ORDER.map(
    (c) => [c, MOVE_CLASS_LABELS[c], streets.filter((s) => s.cls === c).length] as ReportBand,
  ).filter((b) => b[2] > 0);
}

/** Overall accuracy = mean of the per-street accuracies, rounded (matches the mockup's report + Solo). */
export function overallAccuracy(streets: ReportStreet[]): number {
  if (!streets.length) return 0;
  return Math.round(streets.reduce((a, s) => a + s.acc, 0) / streets.length);
}

/** Overall accuracy -> the color band for the big number (mockup renderSReport line 1405). Exported so
 *  Solo (S24) reuses the same threshold for its own report. */
export function badgeFor(overall: number): MoveClass {
  return overall >= 90 ? 'best' : overall >= 80 ? 'exc' : overall >= 68 ? 'inacc' : 'mist';
}

/** Build the full Diagnose end-of-hand report from the four accumulated grades. The verdict copy is
 *  derived from the REAL mix - how many streets landed best, whether a play or only a read slipped, and
 *  which street was the worst - never a fixed string. */
export function buildDiagnoseReport(grades: ResolvedGrade[]): ReportModel {
  const streets = reportStreets(grades);
  const accuracy = overallAccuracy(streets);
  const bands = bandCounts(streets);

  const bestCount = grades.filter((g) => g.verdict.cls === 'best').length;
  const anyPlayWrong = grades.some((g) => !playRight(g));
  // worst street by accuracy (first one, to name the earliest dip).
  let worstIdx = 0;
  streets.forEach((s, i) => {
    if (s.acc < streets[worstIdx].acc) worstIdx = i;
  });
  const worst = (STREET_NAMES[worstIdx] ?? 'that street').toLowerCase();
  const dips = 4 - bestCount;

  let verdict: string;
  let sub: string;
  if (bestCount === grades.length) {
    verdict = 'Textbook, start to finish';
    sub =
      'You read him right on every street and took the best line each time. Same top pair the whole way, three correct actions, because the read kept changing.';
  } else if (!anyPlayWrong) {
    // every play was right; the only slips were reads coming in a touch fast.
    verdict = dips === 1 ? 'Strong hand, one slip' : 'Strong hand, a couple of slips';
    sub =
      `You made all four plays at best. ` +
      (dips === 1
        ? `One rushed read on the ${worst} cost a little.`
        : `A few rushed reads cost a little. The ${worst} slipped furthest.`);
  } else if (accuracy >= 75) {
    verdict = 'Solid, but one misplay';
    sub = `The read mostly held, but the ${worst} play cost you. Replay that decision and read the player first.`;
  } else if (accuracy >= 55) {
    verdict = 'The line slipped';
    sub = `A decision or two got away from you around the ${worst}. Read the player, then act.`;
  } else {
    verdict = 'Rough hand';
    sub = 'Several decisions missed. Replay it and trust the read before the cards.';
  }

  return {
    kicker: 'End of hand',
    h2: 'How you played the hand',
    lede: 'Across all four streets. Graded on the read and the play, not on whether you won.',
    accuracy,
    badgeCls: badgeFor(accuracy),
    verdict,
    sub,
    streets,
    bands,
  };
}
